import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { authFetch } from "../../utils/auth";

/**
 * Session context — what conversation memory holds for THIS session.
 *
 * Body only: it lives inside the results drawer alongside Bugs and Cases, so
 * the drawer owns the header, the tabs and the close button. It used to be a
 * fixed rail pinned beside the copilot, which cost 290px of answer width
 * permanently to show reference material read a few times a session.
 *
 * READ-ONLY on purpose. It is the copilot's own working state, and an engineer
 * correcting it here would be correcting a summary rather than the conversation
 * that produced it — the fix belongs in the chat, where the next turn folds it
 * back in.
 *
 * Fetched from /api/memory/sessions/{id} rather than pushed over the socket:
 * memory is written AFTER the answer streams (conversation_context.observe_bg),
 * so there is no turn event that carries a fresh version of it. Refetching once
 * a turn has settled is what makes this show the state the NEXT turn will read.
 *
 * SERVE-STALE, but only across a refetch. Reopening the drawer used to blank the
 * panel and sit empty for SETTLE_MS before the first row appeared, which read as
 * "the copilot remembers nothing". The last good state is cached per session and
 * painted immediately; the refetch runs behind it and swaps content in when it
 * lands.
 *
 * The cache is NOT authoritative — the server is. A 404 clears both, because the
 * session id lives in sessionStorage and survives a route change, so a cleared or
 * expired session comes back with the same id and nothing else would ever
 * invalidate the entry. Stale-on-404 left the panel showing a finished
 * conversation's context under a session that no longer had any.
 */

// Survives unmount: the drawer tears its tabs down, and losing the cache with
// them would put the empty flash straight back.
const STATE_CACHE = new Map();

// Only what the server can still populate. symptom/environment/attempted/
// decisions were the old typed buckets; the extractor that filled them is off
// (conversation_context: TSE_CONTEXT_SEGMENTS, default false) and the server's
// _SEGMENT_FIELDS is now `ruled_out` alone. Listing the rest kept four rows in
// the code that could never render.
const LIST_FIELDS = [
  ["ruled_out", "Ruled out"],
];

// Memory lands a few seconds after the answer (an LLM extraction runs at read
// time), so an immediate refetch would show the previous turn's state.
const SETTLE_MS = 6000;

const SessionContextPanel = ({ sessionId, turnKey, resetKey = 0, active }) => {
  const [state, setState] = useState(() => STATE_CACHE.get(sessionId) || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Which turn the last fetch was for. Distinguishes "a turn just settled, wait
  // for the write" from "the drawer was reopened", which must paint at once.
  const lastTurnRef = useRef(turnKey);

  // Switching sessions must not show the previous session's memory. No id at all
  // (route left the dashboard, session cleared before one was issued) shows
  // nothing rather than whatever was last cached.
  useEffect(() => {
    setState(sessionId ? STATE_CACHE.get(sessionId) || null : null);
    setError("");
  }, [sessionId]);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError("");
    try {
      const res = await authFetch(`/api/memory/sessions/${encodeURIComponent(sessionId)}`);
      if (res.status === 404) {
        // AUTHORITATIVE, so it clears both the view and the cache. Memory is
        // rewritten in place under one key per session, so 404 means the key does
        // not exist — never "a write is still in flight". Serving stale here is
        // what left a cleared session showing the previous conversation's context:
        // the id is kept in sessionStorage and comes back on a route change, so
        // without this the cache had nothing to invalidate it.
        STATE_CACHE.delete(sessionId);
        setState(null);
        return;
      }
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = await res.json();
      const next = data.state || null;
      STATE_CACHE.set(sessionId, next);
      setState(next);
    } catch (e) {
      // Keep the stale view under the error: a failed refresh is not a reason to
      // throw away context the engineer was reading.
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  // "New chat" reuses the session id (sockets_tse.tse_clear swaps in a fresh
  // TSESession under it), so nothing about the id changes and none of the fetch
  // triggers below fire. Without this the panel kept painting the cleared
  // session's context. Drops it at once rather than waiting for the round trip —
  // the server has already deleted the memory, so there is nothing to keep.
  useEffect(() => {
    if (!resetKey) return;
    STATE_CACHE.delete(sessionId);
    setState(null);
    setError("");
  }, [resetKey, sessionId]);

  // Only while the tab is on screen. The drawer keeps all three tabs mounted,
  // and polling memory for a tab nobody is looking at is a request per turn
  // for nothing.
  useEffect(() => {
    if (!active) return undefined;
    const isNewTurn = turnKey !== lastTurnRef.current;
    lastTurnRef.current = turnKey;
    // Reopening refreshes immediately behind the cached view; only a turn that
    // just landed waits for the extraction to be written.
    const timer = setTimeout(load, isNewTurn ? SETTLE_MS : 0);
    return () => clearTimeout(timer);
  }, [active, turnKey, resetKey, load]);

  const contexts = state?.contexts || [];
  const activeCtx = contexts[state?.active ?? 0] || contexts[0];
  const others = contexts.filter((c) => c !== activeCtx);
  // The running summary. This is what the memory actually keeps now: one entry
  // per couple of exchanges, appended and never rewritten. Read in conversation
  // order — 1-2, then 3-4 — so it tracks how the investigation actually moved.
  // Stored order is already chronological, so this is a straight pass-through.
  const summary = state?.blocks || [];
  const turnCount = (state?.turns || []).length;
  // Anything at all to show. Previously keyed on activeCtx alone, which is why a
  // session with a full summary still rendered the empty state: with the segment
  // extractor off, contexts[] is a shell that only exists to hold eliminations.
  const hasAnything = summary.length > 0 || !!activeCtx || turnCount > 0;

  return (
    <div className="d5-ctx-panel">
      <div className="d5-ctx-panel-bar">
        <span className="d5-ctx-panel-note">
          Read-only — built from the conversation
          {loading && state && <span className="d5-ctx-panel-updating">updating…</span>}
        </span>
        <button
          type="button"
          className="d5-ctxrail-icon"
          title="Refresh"
          onClick={load}
        >
          <RefreshCw size={13} className={loading ? "d5-ctxrail-spin" : ""} />
        </button>
      </div>

      {error && <div className="d5-ctxrail-error">{error}</div>}
      {!hasAnything && (
        <div className="d5-ctxrail-empty">
          Nothing yet — context builds from the conversation as it goes.
        </div>
      )}

      {hasAnything && (
        <>
          {summary.length > 0 && (
            <div className="d5-ctxrail-group">
              <div className="d5-ctxrail-label">What has happened so far</div>
              {summary.map((b, i) => (
                <div key={b.turns || i} className="d5-ctxrail-block">
                  <span className="d5-ctxrail-block-turns">
                    {b.turns ? `Exchanges ${b.turns}` : `Entry ${i + 1}`}
                  </span>
                  <p className="d5-ctxrail-summary">{b.text}</p>
                </div>
              ))}
            </div>
          )}

          {activeCtx?.topic && <div className="d5-ctxrail-topic">{activeCtx.topic}</div>}
          {activeCtx?.summary && <p className="d5-ctxrail-summary">{activeCtx.summary}</p>}

          {LIST_FIELDS.map(([key, label]) => {
            const value = activeCtx?.[key];
            const items = Array.isArray(value) ? value : (value ? [value] : []);
            if (!items.length) return null;
            return (
              <div key={key} className="d5-ctxrail-group">
                <div className="d5-ctxrail-label">{label}</div>
                <ul className="d5-ctxrail-list">
                  {items.map((v, i) => <li key={i}>{v}</li>)}
                </ul>
              </div>
            );
          })}

          {others.filter((c) => c?.topic).length > 0 && (
            <div className="d5-ctxrail-group">
              <div className="d5-ctxrail-label">Also in this session</div>
              <ul className="d5-ctxrail-list">
                {others.filter((c) => c?.topic).map((c, i) => <li key={i}>{c.topic}</li>)}
              </ul>
            </div>
          )}

          <div className="d5-ctxrail-foot">
            {turnCount} exchange{turnCount === 1 ? "" : "s"} recorded
          </div>
        </>
      )}
    </div>
  );
};

export default SessionContextPanel;
